import { action, computed, observable } from 'mobx';

export type Optional<T> = T | undefined;
export type Nullable<T> = T | null;

// Simplified results for service consumers
// More complex services can define more advanced results on their own

export enum ResultKind {
  OK, // Service returned successfully
  USER_ERROR, // Invalid request (i.e. bad values, authentication issues, etc.)
  SYSTEM_ERROR, // System failure (i.e. server down, network problems, etc.)
}

export enum ServiceStatus {
  FRESH,
  LOADING,
  LOADED,
  FAILED,
}

export class OrderedMap<K, V> {
  @observable.deep
  private order: K[];

  @observable.deep
  private readonly data: Map<K, V>;

  constructor(pairs?: [K, V][]) {
    this.order = []; this.data = new Map();
    if (pairs) this.setArray(pairs);
  }

  keyAt(index: number): Optional<K> {
    if (0 <= index && index < this.order.length) {
      return this.order[index];
    }
  }

  get(key: K): Optional<V> {
    return this.data.get(key);
  }

  getAt(index: number): Optional<V> {
    if (0 <= index && index < this.order.length) {
      return this.data.get(this.order[index]);
    }
  }

  has(key: K): boolean {
    return this.data.has(key);
  }

  @action
  set(key: K, value: V): void {
    if (!this.data.has(key)) this.order.push(key);
    this.data.set(key, value);
  }

  @action
  setAt(key: K, value: V, index: number): void {
    const idx = this.order.indexOf(key);
    if (idx !== -1) this.order.splice(idx, 1);
    this.order.splice(index, 0, key);
    this.data.set(key, value);
  }

  @action
  setArray(pairs: [K, V][]): void {
    pairs.forEach(([k, v]) => this.set(k, v));
  }

  @action
  setOrderedMap(map: OrderedMap<K, V>): void {
    map.forEach((v, k) => this.set(k, v));
  }

  @action
  delete(key: K): void {
    const idx = this.order.indexOf(key);
    if (idx !== -1) {
      this.order.splice(idx, 1);
      this.data.delete(key);
    }
  }

  @action
  deleteAt(index: number): void {
    if (0 <= index && index < this.order.length) {
      this.delete(this.order[index]);
    }
  }

  @action
  pop(key: K): Optional<V> {
    const val = this.get(key);
    this.delete(key);
    return val;
  }

  @action
  popAt(index: number): Optional<V> {
    if (0 <= index && index < this.order.length) {
      return this.pop(this.order[index]);
    }
  }

  @action
  sort(compare: (a: V, b: V) => number): void {
    const order = [...this.order];
    order.sort((ak, bk) => compare(
      this.data.get(ak)!,
      this.data.get(bk)!,
    ));
    this.order = order;
  }

  @action
  reverse(): this {
    this.order = this.order.reverse();
    return this;
  }

  every(callback: (value: V, key: K, map: this) => boolean): boolean {
    return this.order.every((k) => callback(this.data.get(k)!, k, this));
  }

  find(callback: (value: V, key: K, map: this) => boolean): Optional<V> {
    const found = this.order.find((k) => callback(this.data.get(k)!, k, this));
    return found !== undefined ? this.data.get(found) : undefined;
  }

  filter(callback: (value: V, key: K, map: this) => boolean): this {
    return new (this.constructor as new (pairs?: [K, V][]) => this)(
      this.order.filter((k) => callback(this.data.get(k)!, k, this))
                .map((k) => [k, this.data.get(k)!])
    );
  }

  forEach(callback: (value: V, key: K, map: this) => void): void {
    this.data.forEach((v, k) => callback(v, k, this));
  }

  map<T>(callback: (value: V, key: K, index: number, map: this) => T): T[] {
    return this.order.map((k, i) => callback(this.data.get(k)!, k, i, this));
  }

  reduce<T>(callback: (accumulator: T, value: V, key: K, map: this) => T, initial: T): T {
    return this.order.reduce((a, k) => callback(a, this.data.get(k)!, k, this), initial);
  }

  some(callback: (value: V, key: K, map: this) => boolean): boolean {
    return this.order.some((k) => callback(this.data.get(k)!, k, this));
  }

  slice(start?: number, end?: number): this {
    return new (this.constructor as new (pairs?: [K, V][]) => this)(
      this.order.slice(start, end).map((k) => [k, this.data.get(k)!])
    );
  }

  sorted(compare: (a: V, b: V) => number): this {
    const sorted = this.slice();
    sorted.sort(compare);
    return sorted;
  }

  @computed
  get keys(): K[] {
    return [...this.order];
  }

  @computed
  get values(): V[] {
    return this.order.map((k) => this.data.get(k)!);
  }

  @computed
  get pairs(): [K, V][] {
    return this.order.map((k) => [k, this.data.get(k)!]);
  }

  @computed
  get size(): number {
    return this.data.size;
  }
}
